import { AlkLogger } from '@alk/core';
import { AUTHENTICATION_FACTORY, IAuthenticationFactory } from '../authentication';
import { getRiskApiUrl } from './sse-for-limits.utils';
import { inject, injectable } from 'inversify';
import { ISelectedClientService, SELECTED_CLIENT_SERVICE } from '../selected-client';
import {
    IServerSentEventsClientService,
    SERVER_SENT_EVENTS_CLIENT_SERVICE,
} from '@alk/sse';
import { ISSEForLimitsService } from './sse-for-limits.interface';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@injectable()
export class SSEForLimitsService implements ISSEForLimitsService {

    @inject(SELECTED_CLIENT_SERVICE) private readonly selectedClientSrv: ISelectedClientService;
    @inject(AUTHENTICATION_FACTORY) private readonly authFactory: IAuthenticationFactory;
    @inject(SERVER_SENT_EVENTS_CLIENT_SERVICE) private readonly sseClient: IServerSentEventsClientService;

    private isInitialized = false;
    private readonly destroy$: Subject<boolean> = new Subject<boolean>();

    private initialization(): void {
        this.isInitialized = true;

        let now;
        this.sseClient.onerror = (event) => {
            AlkLogger.error('[SSEForLimitsService#initialization] Error', event);
        };

        this.sseClient.onclose = () => {
            const secs = Math.floor((Date.now() - now) / 1000);
            now = Date.now();
            AlkLogger.debug(`[SSEForLimitsService#initialization] Close after ${ secs } seconds`);
        };

        this.sseClient.onopen = (event) => {
            now = Date.now();
            AlkLogger.debug('[SSEForLimitsService#initialization] Open', event);
        };

        this.sseClient.onmessage = (event) => {
            AlkLogger.debug('[SSEForLimitsService#initialization] Message Type', event.type ?? '');
            AlkLogger.debug('[SSEForLimitsService#initialization] Message Data', event.data ?? '');
        };
    }

    async init(): Promise<void> {
        if (!this.isInitialized) {
            this.initialization();
        }

        const authSrv = this.authFactory();
        const token = await authSrv.getToken();

        this.selectedClientSrv.asObservable()
            .pipe(
                takeUntil(this.destroy$),
            )
            .subscribe(async (selectedClient) => {
                this.sseClient.close();
                this.sseClient.reset();

                if (Boolean(selectedClient)) {
                    const url = getRiskApiUrl(selectedClient.externalRef ?? selectedClient.name);

                    await this.sseClient.init(url,
                        {
                            headers: new Headers({ 'x-authorization-sfx': token.access_token }),
                        });
                }
            });
    }

    stop(): void {
        this.destroy$.next(true);

        this.sseClient.close();
        this.sseClient.reset();
    }

    addEventListener(eventType: string, callback: (message?: MessageEvent) => void): void {
        this.sseClient.addEventListener(eventType, callback);
    }

    removeEventListener(eventType: string, callback: (message?: MessageEvent) => void): void {
        this.sseClient.removeEventListener(eventType, callback);
    }
}
